import React from 'react';
import { Stack } from '@mui/material';
import { SalonDashboardReport, reportApi } from '../../../services/reportService';
import { StatTile } from '../../../components/ui';
import { countLabel } from '../../../utils/format';
import { DateRange } from './StatsDateRange';
import {
  SectionCard,
  CHART_COLORS,
  formatPLN,
  formatNumber,
  formatPct,
  useAsyncData,
  pickGranularity,
  EmptyHint,
  RankList,
  StatTileGrid,
  StatsRow,
} from './statsShared';
import { RevenueAreaChart, DonutChart } from './ChartKit';

interface Props {
  salonId: string;
  range: DateRange;
  data: SalonDashboardReport;
}

export const PanelTab: React.FC<Props> = ({ salonId, range, data }) => {
  const granularity = pickGranularity(range.dateFrom, range.dateTo);
  const { data: ts } = useAsyncData(
    () => reportApi.getTimeseries(salonId, range.dateFrom, range.dateTo, granularity),
    [salonId, range.dateFrom, range.dateTo, granularity],
  );

  const avgOccupancy = data.staffPerformance.length
    ? Math.round(data.staffPerformance.reduce((s, x) => s + x.occupancy, 0) / data.staffPerformance.length)
    : 0;

  const pay = data.paymentBreakdown;
  const payData = [
    { name: 'Karta', value: pay.card, color: CHART_COLORS[0] },
    { name: 'Gotówka', value: pay.cash, color: CHART_COLORS[1] },
    { name: 'BLIK', value: pay.blik, color: CHART_COLORS[2] },
    { name: 'Przelew', value: pay.transfer, color: CHART_COLORS[3] },
  ];

  const maxServiceRevenue = Math.max(1, ...data.topServices.map((s) => s.revenue));

  return (
    <Stack gap={2}>
      <StatTileGrid>
        <StatTile
          label="Przychód"
          value={formatPLN(data.revenue.total)}
          change={Math.round(data.revenue.change)}
          changeLabel="niż poprzednio"
        />
        <StatTile
          label="Wizyty"
          value={formatNumber(data.bookings.total)}
          hint={`Zakończone: ${formatNumber(data.bookings.completed)}`}
        />
        <StatTile
          label="Klienci"
          value={formatNumber(data.clients.total)}
          hint={`Nowi: ${formatNumber(data.clients.new)}`}
        />
        <StatTile label="Średnie obłożenie" value={formatPct(avgOccupancy)} progress={avgOccupancy} hint="Według grafiku pracowników" />
      </StatTileGrid>

      <StatsRow template="2fr 1fr">
        <SectionCard title="Przychód w czasie" subtitle={granularity === 'week' ? 'Sumy tygodniowe' : 'Sumy dzienne'}>
          {ts && ts.buckets.some((b) => b.revenue > 0) ? (
            <RevenueAreaChart data={ts.buckets.map((b) => ({ label: b.label, revenue: b.revenue }))} height={320} />
          ) : (
            <EmptyHint description="Wybierz dłuższy okres albo inny zakres dat.">Brak przychodu w wybranym okresie</EmptyHint>
          )}
        </SectionCard>
        <SectionCard title="Metody płatności">
          {payData.some((p) => p.value > 0) ? <DonutChart data={payData} /> : <EmptyHint>Brak wpłat w wybranym okresie</EmptyHint>}
        </SectionCard>
      </StatsRow>

      <SectionCard title="Najlepsze usługi" subtitle="Według przychodu w wybranym okresie">
        {data.topServices.length ? (
          <RankList
            items={data.topServices.slice(0, 6).map((s) => ({
              id: s.id,
              label: s.name,
              sub: countLabel(s.count, 'wizyta', 'wizyty', 'wizyt'),
              value: formatPLN(s.revenue),
              ratio: s.revenue / maxServiceRevenue,
            }))}
          />
        ) : (
          <EmptyHint>Brak usług w wybranym okresie</EmptyHint>
        )}
      </SectionCard>
    </Stack>
  );
};
